import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCartPlus, faChevronLeft, faChevronRight, faSearch } from "@fortawesome/free-solid-svg-icons";
import { Button, Card, Col, Form, InputGroup, Modal, Row } from "@themesberg/react-bootstrap";
import ContentLoader from "react-content-loader";
import { useHistory, useParams } from "react-router-dom";

import { getVendorProductById } from "../api/vendor.api";
import { useToast } from "../components/ToastProvider";
import { Routes } from "../routes";
import { resolveAssetUrl } from "../utils/assetUrl";
import { addCartItem } from "../utils/cartStorage";

const sanitizeQuantityInput = (value) => value.replace(/[^\d]/g, "");

const ProductDetailsSkeleton = () => (
  <div>
    <Card border="light" className="shadow-sm mb-4">
      <Card.Body>
        <Row>
          <Col xs={12} lg={5} className="mb-4 mb-lg-0">
            <ContentLoader speed={2} viewBox="0 0 360 360" backgroundColor="#f3f3f3" foregroundColor="#e0e0e0">
              <rect x="0" y="0" width="360" height="360" rx="16" />
            </ContentLoader>
          </Col>
          <Col xs={12} lg={7}>
            <ContentLoader speed={2} viewBox="0 0 400 200" backgroundColor="#f3f3f3" foregroundColor="#e0e0e0">
              <rect x="0" y="0" width="250" height="24" rx="4" />
              <rect x="0" y="40" width="380" height="14" rx="4" />
              <rect x="0" y="65" width="320" height="12" rx="4" />
              <rect x="0" y="90" width="280" height="12" rx="4" />
              <rect x="0" y="130" width="180" height="16" rx="4" />
            </ContentLoader>
          </Col>
        </Row>
      </Card.Body>
    </Card>
    <Card border="light" className="shadow-sm">
      <Card.Body>
        <ContentLoader speed={2} viewBox="0 0 400 40" backgroundColor="#f3f3f3" foregroundColor="#e0e0e0">
          <rect x="0" y="0" width="150" height="20" rx="4" />
          <rect x="250" y="0" width="150" height="30" rx="4" />
        </ContentLoader>
        <Row className="mt-3">
          {[1, 2].map((i) => (
            <Col xs={12} lg={6} key={i} className="mb-3">
              <Card border="light" className="h-100 shadow-sm">
                <Card.Body>
                  <ContentLoader speed={2} viewBox="0 0 350 250" backgroundColor="#f3f3f3" foregroundColor="#e0e0e0">
                    <rect x="0" y="0" width="350" height="180" rx="8" />
                    <rect x="0" y="195" width="150" height="16" rx="4" />
                    <rect x="0" y="220" width="100" height="12" rx="4" />
                    <rect x="180" y="195" width="80" height="14" rx="4" />
                    <rect x="280" y="193" width="70" height="32" rx="4" />
                  </ContentLoader>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Card.Body>
    </Card>
  </div>
);

export default () => {
  const history = useHistory();
  const { id } = useParams();
  const { showToast } = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeProductImageIndex, setActiveProductImageIndex] = useState(0);
  const [activeVariantImageIndexes, setActiveVariantImageIndexes] = useState({});
  const [variantQuantities, setVariantQuantities] = useState({});
  const [variantSearch, setVariantSearch] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImages, setModalImages] = useState([]);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  useEffect(() => {
    const loadProduct = async () => {
      setLoading(true);

      try {
        const response = await getVendorProductById(id);
        setProduct(response);
        setActiveProductImageIndex(0);
        setActiveVariantImageIndexes(
          Array.isArray(response?.variants)
            ? response.variants.reduce((accumulator, variant) => {
              accumulator[variant.id] = 0;
              return accumulator;
            }, {})
            : {}
        );
        setVariantQuantities(
          Array.isArray(response?.variants)
            ? response.variants.reduce((accumulator, variant) => {
              accumulator[variant.id] = "1";
              return accumulator;
            }, {})
            : {}
        );
      } catch (error) {
        showToast({ message: error.message || "Failed to load product details", variant: "danger" });
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, showToast]);

  const productImages = Array.isArray(product?.images) ? product.images : [];
  const filteredVariants = Array.isArray(product?.variants)
    ? product.variants.filter((variant) => {
      const searchValue = variantSearch.trim().toLowerCase();
      if (!searchValue) {
        return true;
      }

      return [variant.variant_name, variant.sku]
        .some((value) => String(value || "").toLowerCase().includes(searchValue));
    })
    : [];
  const activeImage = modalImages[modalImageIndex] || null;

  const openImageModal = (images, index) => {
    setModalImages(images);
    setModalImageIndex(index);
    setShowImageModal(true);
  };

  const showPreviousImage = () => {
    if (!modalImages.length) return;
    setModalImageIndex((currentIndex) => (currentIndex === 0 ? modalImages.length - 1 : currentIndex - 1));
  };

  const showNextImage = () => {
    if (!modalImages.length) return;
    setModalImageIndex((currentIndex) => (currentIndex === modalImages.length - 1 ? 0 : currentIndex + 1));
  };

  const handleAddToCart = (variant) => {
    if (!product || !variant) {
      showToast({ message: "Variant is required.", variant: "warning" });
      return;
    }

    const normalizedQuantity = Number(variantQuantities[variant.id] || 0);
    if (!normalizedQuantity || normalizedQuantity < 1) {
      showToast({ message: "Quantity must be at least 1.", variant: "warning" });
      return;
    }

    if (normalizedQuantity > Number(variant.stock || 0)) {
      showToast({ message: "Selected quantity exceeds available stock.", variant: "warning" });
      return;
    }

    addCartItem({
      product_id: product.id,
      product_name: product.name,
      product_image_url: product.image_url,
      variant_id: variant.id,
      variant_name: variant.variant_name,
      variant_image_url: Array.isArray(variant.images) && variant.images.length > 0 ? variant.images[0].image_url : (variant.image_url || ""),
      sku: variant.sku,
      stock: Number(variant.stock || 0),
      quantity: normalizedQuantity
    });

    showToast({ message: "Added to cart.", variant: "success" });
  };

  return (
    <div className="py-4">
      <div className="px-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h3 className="mb-0">Product Details</h3>
          </div>
          <Button variant="outline-secondary" onClick={() => history.push(Routes.Products.path)} size="sm">
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
            Back
          </Button>
        </div>
        <p className="text-muted mb-4 small">Review the product and choose the right variant.</p>
      </div>

      {loading ? (
        <ProductDetailsSkeleton />
      ) : !product ? null : (
        <>
          <Card border="light" className="shadow-sm mb-4">
            <Card.Body>
              <Row>
                <Col xs={12} lg={5} className="mb-4 mb-lg-0">
                  {productImages.length > 0 ? (
                    <div>
                      <div
                        style={{
                          width: "100%",
                          height: "360px",
                          borderRadius: "1rem",
                          overflow: "hidden",
                          backgroundColor: "#f8f9fa"
                        }}
                      >
                        <img
                          src={resolveAssetUrl(productImages[activeProductImageIndex]?.image_url)}
                          alt={product.name}
                          onClick={() => openImageModal(productImages, activeProductImageIndex)}
                          style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                        />
                      </div>
                      {productImages.length > 1 ? (
                        <div className="d-flex flex-wrap gap-2 mt-3">
                          {productImages.map((image, index) => (
                            <img
                              key={image.id || image.image_url}
                              src={resolveAssetUrl(image.image_url)}
                              alt={`${product.name} ${index + 1}`}
                              onClick={() => setActiveProductImageIndex(index)}
                              style={{
                                width: "72px",
                                height: "72px",
                                objectFit: "cover",
                                borderRadius: "0.75rem",
                                cursor: "pointer",
                                border: index === activeProductImageIndex ? "2px solid #0d6efd" : "2px solid transparent"
                              }}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="border rounded d-flex align-items-center justify-content-center text-muted" style={{ minHeight: "220px" }}>
                      No image
                    </div>
                  )}
                </Col>
                <Col xs={12} lg={7}>
                  <h4>{product.name}</h4>
                  <p className="text-muted mb-4">{product.description || "No description available."}</p>
                  <div className="small text-muted">
                    Choose a variant below to set quantity and add it to cart.
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card border="light" className="shadow-sm">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h5 className="mb-0">Variants</h5>
                <div style={{ minWidth: "280px" }}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search variants by name or SKU"
                      value={variantSearch}
                      onChange={(event) => setVariantSearch(event.target.value)}
                    />
                  </InputGroup>
                </div>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                {filteredVariants.length ? filteredVariants.map((variant) => (
                  <Col xs={12} lg={6} className="mb-3" key={variant.id}>
                    <Card border="light" className="h-100 shadow-sm">
                      <Card.Body>
                        <div
                          className="mb-3 rounded"
                          style={{
                            height: "220px",
                            overflow: "hidden",
                            backgroundColor: "#f8f9fa"
                          }}
                        >
                          {Array.isArray(variant.images) && variant.images.length > 0 ? (
                            <img
                              src={resolveAssetUrl(variant.images[activeVariantImageIndexes[variant.id] || 0]?.image_url)}
                              alt={variant.variant_name}
                              onClick={() => openImageModal(variant.images, activeVariantImageIndexes[variant.id] || 0)}
                              style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                            />
                          ) : (
                            <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                              No image
                            </div>
                          )}
                        </div>
                        {Array.isArray(variant.images) && variant.images.length > 1 ? (
                          <div className="d-flex flex-wrap gap-2 mb-3">
                            {variant.images.map((image) => (
                              <img
                                key={image.id || image.image_url}
                                src={resolveAssetUrl(image.image_url)}
                                alt={variant.variant_name}
                                onClick={() => setActiveVariantImageIndexes((currentData) => ({
                                  ...currentData,
                                  [variant.id]: variant.images.findIndex((variantImage) => (variantImage.id || variantImage.image_url) === (image.id || image.image_url))
                                }))}
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                  borderRadius: "0.75rem",
                                  border: (variant.images[activeVariantImageIndexes[variant.id] || 0]?.id || variant.images[activeVariantImageIndexes[variant.id] || 0]?.image_url) === (image.id || image.image_url)
                                    ? "2px solid #0d6efd"
                                    : "1px solid #e9ecef",
                                  cursor: "pointer"
                                }}
                              />
                            ))}
                          </div>
                        ) : null}
                        <div>
                          <h6 className="mb-2">{variant.variant_name}</h6>
                          <div className="small text-muted mb-1">SKU: {variant.sku}</div>
                          <div className="small text-muted mb-3">Available Stock: {variant.stock}</div>
                          <Row className="align-items-end">
                            <Col xs={12} md={5} className="mb-3">
                              <Form.Group>
                                <Form.Label>Quantity</Form.Label>
                                <Form.Control
                                  type="text"
                                  inputMode="numeric"
                                  value={variantQuantities[variant.id] ?? "1"}
                                  onChange={(event) => {
                                    const nextValue = sanitizeQuantityInput(event.target.value);
                                    setVariantQuantities((currentData) => ({
                                      ...currentData,
                                      [variant.id]: nextValue
                                    }));
                                  }}
                                  disabled={Number(variant.stock || 0) < 1}
                                />
                              </Form.Group>
                            </Col>
                            <Col xs={12} md={7} className="mb-3">
                              <Button
                                variant="primary"
                                onClick={() => handleAddToCart(variant)}
                                disabled={Number(variant.stock || 0) < 1}
                              >
                                <FontAwesomeIcon icon={faCartPlus} className="me-2" />
                                Add to Cart
                              </Button>
                            </Col>
                          </Row>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                )) : (
                  <Col xs={12} className="text-muted">
                    {variantSearch.trim() ? "No variants matched your search." : "No variants available."}
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        </>
      )}

      <Modal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        centered
        size="xl"
        dialogClassName="m-0 w-100 mw-100 h-100 d-flex align-items-center justify-content-center"
        contentClassName="bg-transparent border-0 shadow-none"
      >
        <Modal.Body className="p-0" style={{ backgroundColor: "transparent" }}>
          {activeImage ? (
            <div className="position-relative d-flex align-items-center justify-content-center" style={{ minHeight: "80vh" }}>
              <Button
                type="button"
                variant="link"
                onClick={() => setShowImageModal(false)}
                className="p-0 text-white"
                style={{
                  position: "absolute",
                  top: "1rem",
                  right: "1rem",
                  zIndex: 2,
                  width: "48px",
                  height: "48px",
                  borderRadius: "999px",
                  backgroundColor: "rgba(17, 24, 39, 0.6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5rem",
                  lineHeight: 1,
                  textDecoration: "none"
                }}
              >
                x
              </Button>
              <img
                src={resolveAssetUrl(activeImage.image_url)}
                alt={product?.name || "Product image"}
                style={{ width: "100%", maxHeight: "75vh", objectFit: "contain", borderRadius: "0.75rem" }}
              />
              {modalImages.length > 1 ? (
                <>
                  <Button
                    type="button"
                    variant="link"
                    onClick={showPreviousImage}
                    className="p-0 text-white"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "1.5rem",
                      transform: "translateY(-50%)",
                      width: "52px",
                      height: "52px",
                      borderRadius: "999px",
                      backgroundColor: "rgba(17, 24, 39, 0.55)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textDecoration: "none"
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    onClick={showNextImage}
                    className="p-0 text-white"
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: "1.5rem",
                      transform: "translateY(-50%)",
                      width: "52px",
                      height: "52px",
                      borderRadius: "999px",
                      backgroundColor: "rgba(17, 24, 39, 0.55)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textDecoration: "none"
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronRight} size="lg" />
                  </Button>
                </>
              ) : null}
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
};
